import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { logCategoryOperation, sanitizeCategoryName, normalizeCategoryNameForComparison } from '@/lib/categoryUtils';
import { getAvailableSizesByProduct } from '@/lib/stockReservationService';
import type { Product } from '@/types';
import type { ProductFilters } from '@/components/product/ProductSearch';

interface PriceRangeDefaults {
  minPrice: number;
  maxPrice: number;
}

interface UseServerSideProductSearchReturn {
  searchProducts: (userId: string, filters: ProductFilters, priceDefaults?: PriceRangeDefaults, inventoryEnabled?: boolean) => Promise<Product[]>;
  loading: boolean;
  error: string | null;
}

export function useServerSideProductSearch(): UseServerSideProductSearchReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const searchProducts = useCallback(async (userId: string, filters: ProductFilters, priceDefaults?: PriceRangeDefaults, inventoryEnabled = false): Promise<Product[]> => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setLoading(true);
      setError(null);

      logCategoryOperation('SERVER_SIDE_SEARCH_START', {
        userId,
        filters: {
          query: filters.query,
          status: filters.status,
          category: filters.category,
          brand: filters.brand,
          gender: filters.gender,
          sizes: filters.sizes,
          condition: filters.condition,
          minPrice: filters.minPrice,
          maxPrice: filters.maxPrice
        }
      });

      let query = supabase
        .from('products')
        .select(`
          id,
          slug,
          title,
          price,
          discounted_price,
          is_starting_price,
          short_description,
          status,
          category,
          brand,
          gender,
          condition,
          featured_image_url,
          colors,
          sizes,
          flavors,
          has_weight_variants,
          min_variant_price,
          max_variant_price,
          display_order,
          external_checkout_url,
          has_tiered_pricing,
          min_tiered_price,
          max_tiered_price,
          is_visible_on_storefront,
          track_inventory,
          stock_quantity,
          low_stock_threshold,
          product_images (
            id,
            url,
            associated_color
          )
        `)
        .eq('user_id', userId)
        .eq('is_visible_on_storefront', true);

      // Apply status filter
      if (filters.status && filters.status !== 'todos') {
        query = query.eq('status', filters.status);
      }

      // Apply condition filter
      if (filters.condition && filters.condition !== 'todos') {
        query = query.eq('condition', filters.condition);
      }

      // Execute query
      const { data: products, error: queryError } = await query
        .order('display_order', { ascending: true, nullsLast: true })
        .order('id', { ascending: false })
        .limit(5000);

      if (queryError) {
        throw queryError;
      }

      let filteredProducts = products || [];
      console.log(`[Search] Produtos do servidor (visíveis): ${filteredProducts.length}`);

      // Apply price range filter with tiered pricing support
      const defaultMinPrice = priceDefaults?.minPrice ?? 0;
      const defaultMaxPrice = priceDefaults?.maxPrice ?? 5000;
      const isPriceFilterActive =
        (filters.minPrice !== undefined && filters.minPrice !== defaultMinPrice) ||
        (filters.maxPrice !== undefined && filters.maxPrice !== defaultMaxPrice);

      if (isPriceFilterActive) {
        const minPrice = filters.minPrice ?? defaultMinPrice;
        const maxPrice = filters.maxPrice ?? defaultMaxPrice;

        const before = filteredProducts.length;
        filteredProducts = filteredProducts.filter(product => {
          if (product.has_tiered_pricing) {
            // For tiered pricing products, check if any tier price falls within range
            const tierMin = product.min_tiered_price ?? Infinity;
            const tierMax = product.max_tiered_price ?? 0;

            // Include if tier price range overlaps with filter range
            return tierMin <= maxPrice && tierMax >= minPrice;
          } else {
            // For regular products, check discounted_price or price
            const productPrice = product.discounted_price ?? product.price ?? 0;
            return productPrice >= minPrice && productPrice <= maxPrice;
          }
        });
        console.log(`[Search] Após filtro de preço: ${filteredProducts.length} (era ${before})`);
      }

      // Apply text search (title and short_description)
      if (filters.query && filters.query.trim()) {
        const searchQuery = filters.query.toLowerCase();
        const before = filteredProducts.length;
        filteredProducts = filteredProducts.filter(product =>
          (product.title?.toLowerCase().includes(searchQuery)) ||
          (product.short_description?.toLowerCase().includes(searchQuery)) ||
          (product.brand?.toLowerCase().includes(searchQuery))
        );
        console.log(`[Search] Após filtro de texto: ${filteredProducts.length} (era ${before})`);
      }

      // Apply category filter (client-side to handle array matching)
      if (filters.category && filters.category !== 'todos') {
        const targetNormalized = normalizeCategoryNameForComparison(filters.category);
        const before = filteredProducts.length;
        filteredProducts = filteredProducts.filter(product => {
          if (!product.category || !Array.isArray(product.category)) {
            return false;
          }
          return product.category.some(cat => {
            const sanitized = sanitizeCategoryName(cat);
            const normalized = normalizeCategoryNameForComparison(sanitized);
            return normalized === targetNormalized;
          });
        });
        console.log(`[Search] Após filtro de categoria "${filters.category}": ${filteredProducts.length} (era ${before})`);
        console.log(`[Search] Categoria normalizada buscada: "${targetNormalized}"`);
      }

      // Apply brand filter
      if (filters.brand && filters.brand !== 'todos') {
        const before = filteredProducts.length;
        filteredProducts = filteredProducts.filter(product =>
          product.brand?.toLowerCase() === filters.brand.toLowerCase()
        );
        console.log(`[Search] Após filtro de marca: ${filteredProducts.length} (era ${before})`);
      }

      // Apply gender filter
      if (filters.gender && filters.gender !== 'todos') {
        const before = filteredProducts.length;
        filteredProducts = filteredProducts.filter(product =>
          product.gender?.toLowerCase() === filters.gender.toLowerCase()
        );
        console.log(`[Search] Após filtro de gênero: ${filteredProducts.length} (era ${before})`);
      }

      // Apply size filter — stock-aware for products that track inventory,
      // so a declared size with zero stock left doesn't still match.
      if (filters.sizes && filters.sizes !== 'todos') {
        const before = filteredProducts.length;
        const targetSize = filters.sizes;

        const hasDeclaredSize = (product: (typeof filteredProducts)[number]) =>
          !!product.sizes && Array.isArray(product.sizes) && product.sizes.some(size => size === targetSize);

        const trackedIds = inventoryEnabled
          ? filteredProducts.filter(p => p.track_inventory && hasDeclaredSize(p)).map(p => p.id)
          : [];

        const availableSizesByProduct = trackedIds.length > 0
          ? await getAvailableSizesByProduct(trackedIds)
          : new Map<string, Set<string>>();

        filteredProducts = filteredProducts.filter(product => {
          if (!hasDeclaredSize(product)) return false;
          const variantSizes = availableSizesByProduct.get(product.id);
          if (inventoryEnabled && product.track_inventory && variantSizes) {
            // Product participates in the size grid — trust actual stock over the declared list.
            return variantSizes.has(targetSize);
          }
          // No size-level stock data for this product (not tracked, or tracks
          // via flat quantity only) — fall back to the declared sizes check.
          return true;
        });

        console.log(`[Search] Após filtro de tamanho: ${filteredProducts.length} (era ${before})`);
      }

      if (abortController.signal.aborted) {
        return [];
      }

      console.log(`[Search] Resultado final: ${filteredProducts.length} produtos`);
      logCategoryOperation('SERVER_SIDE_SEARCH_COMPLETE', {
        resultsCount: filteredProducts.length,
        appliedFilters: Object.entries(filters).filter(([_, v]) => v && v !== 'todos')
      });

      setLoading(false);
      return filteredProducts;

    } catch (err: any) {
      if (abortController.signal.aborted) {
        return [];
      }
      const errorMessage = err.message || 'Error searching products';
      logCategoryOperation('SERVER_SIDE_SEARCH_ERROR', err);
      setError(errorMessage);
      setLoading(false);
      return [];
    }
  }, []);

  return {
    searchProducts,
    loading,
    error
  };
}
